import React, { useState, useEffect, useRef } from 'react';
import {
  Wifi,
  WifiOff,
  Video,
  VideoOff,
  Mic,
  MicOff,
  PhoneCall,
  PhoneOff,
  Copy,
  Check,
  QrCode,
  ShieldCheck,
  ShieldAlert,
  Info,
  Clock,
  Radio,
  RefreshCw,
  Layers,
  ArrowRight,
  UserCheck,
  User,
  Users,
  AlertTriangle,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2
} from 'lucide-react';
import QRCode from 'qrcode';
import { webrtcMeetingService } from '@/services/webrtcMeetingService';
import { WebRTCConnectionState } from '@/types';
import { formatDuration } from '@/utils/helpers';

interface OnlineMeetingRoomProps {
  localStream: MediaStream | null;
  onRemoteStreamReady: (stream: MediaStream | null) => void;
  onConsentStatusChange: (granted: boolean) => void;
  isRecordingActive: boolean;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  hostName?: string;
  guestName?: string;
  onRoleChange?: (role: 'host' | 'guest') => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

export const OnlineMeetingRoom: React.FC<OnlineMeetingRoomProps> = ({
  localStream,
  onRemoteStreamReady,
  onConsentStatusChange,
  isRecordingActive,
  onStartRecording,
  onStopRecording,
  hostName = 'Host',
  guestName = 'Guest',
  onRoleChange,
  isFullscreen = false,
  onToggleFullscreen
}) => {
  // Role: 'host' (Create Room) or 'guest' (Join Room)
  const [role, setRole] = useState<'host' | 'guest'>('host');

  const handleSelectRole = (newRole: 'host' | 'guest') => {
    setRole(newRole);
    onRoleChange?.(newRole);
  };

  // Signaling mode: 'anydesk' (6-digit ID) or 'manual' (Serverless Offer/Answer/QR)
  const [signalingMode, setSignalingMode] = useState<'anydesk' | 'manual'>('anydesk');

  // AnyDesk Mode State
  const [meetingCode, setMeetingCode] = useState<string>('');
  const [inputMeetingCode, setInputMeetingCode] = useState<string>('');
  const [copiedCode, setCopiedCode] = useState(false);

  // Manual Signaling State
  const [generatedOfferCode, setGeneratedOfferCode] = useState<string>('');
  const [inputOfferCode, setInputOfferCode] = useState<string>('');
  const [generatedAnswerCode, setGeneratedAnswerCode] = useState<string>('');
  const [inputAnswerCode, setInputAnswerCode] = useState<string>('');
  const [manualStep, setManualStep] = useState<'initial' | 'offer_created' | 'answer_created' | 'connected'>('initial');

  // Connection & Call State
  const [connectionStatus, setConnectionStatus] = useState<WebRTCConnectionState>('idle');
  const [rttLatencyMs, setRttLatencyMs] = useState<number | null>(null);
  const [callDurationSec, setCallDurationSec] = useState<number>(0);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [hostVideoMirrored, setHostVideoMirrored] = useState(false);
  const [guestVideoMirrored, setGuestVideoMirrored] = useState(false);
  const [remoteMediaState, setRemoteMediaState] = useState<{ audioMuted: boolean; videoOff: boolean }>({
    audioMuted: false,
    videoOff: false
  });

  // Modals & Prompts
  const [incomingCallModal, setIncomingCallModal] = useState<{
    callerId: string;
    accept: () => void;
    decline: () => void;
  } | null>(null);

  const [consentRequestModal, setConsentRequestModal] = useState<{
    hostName: string;
  } | null>(null);

  const [consentGranted, setConsentGranted] = useState(false);
  const [isRequestingConsent, setIsRequestingConsent] = useState(false);
  const [consentDelivered, setConsentDelivered] = useState(false);
  const [qrModalPayload, setQrModalPayload] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [activeLocalStream, setActiveLocalStream] = useState<MediaStream | null>(localStream);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isAudioAutoplayBlocked, setIsAudioAutoplayBlocked] = useState(false);

  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const hostVideoRef = useRef<HTMLVideoElement | null>(null);
  const guestVideoRef = useRef<HTMLVideoElement | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<any>(null);
  const activeLocalStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    activeLocalStreamRef.current = activeLocalStream;
  }, [activeLocalStream]);

  // Generate a friendly AnyDesk-style 6-digit code on mount
  useEffect(() => {
    const randomCode = Math.floor(100000 + Math.random() * 900000).toString();
    setMeetingCode(randomCode);
  }, []);

  // Sync prop changes into activeLocalStream only when tracks actually differ
  useEffect(() => {
    if (localStream) {
      setActiveLocalStream((prev) => {
        if (prev) {
          const prevVideo = prev.getVideoTracks()[0]?.id;
          const nextVideo = localStream.getVideoTracks()[0]?.id;
          const prevAudio = prev.getAudioTracks()[0]?.id;
          const nextAudio = localStream.getAudioTracks()[0]?.id;
          if (prevVideo === nextVideo && prevAudio === nextAudio) {
            return prev;
          }
        }
        return localStream;
      });
    }
  }, [localStream]);

  // Ensure local stream has active audio and video tracks with broadcast-grade 1080p quality
  const ensureLocalStream = async (): Promise<MediaStream> => {
    let stream = activeLocalStream || localStream;
    const hasLiveVideo = stream && stream.getVideoTracks().some(t => t.readyState === 'live');
    const hasLiveAudio = stream && stream.getAudioTracks().some(t => t.readyState === 'live');

    if (stream && hasLiveVideo && hasLiveAudio) {
      stream.getVideoTracks().forEach(t => {
        try { t.contentHint = 'detail'; } catch {}
      });
      return stream;
    }

    try {
      console.log('[OnlineMeetingRoom] Requesting broadcast-grade local camera & microphone...');
      const acquired = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          frameRate: { ideal: 30, min: 24 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 2
        }
      });
      acquired.getVideoTracks().forEach(t => {
        try { t.contentHint = 'detail'; } catch {}
      });
      setActiveLocalStream(acquired);
      webrtcMeetingService.updateLocalStream(acquired);
      return acquired;
    } catch (err: any) {
      console.warn('[OnlineMeetingRoom] Full HD acquisition failed, falling back to standard HD:', err);
      try {
        const fallback = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
          audio: { echoCancellation: true, noiseSuppression: true }
        });
        fallback.getVideoTracks().forEach(t => {
          try { t.contentHint = 'detail'; } catch {}
        });
        setActiveLocalStream(fallback);
        webrtcMeetingService.updateLocalStream(fallback);
        return fallback;
      } catch (fallbackErr: any) {
        console.warn('[OnlineMeetingRoom] Fallback acquisition error:', fallbackErr);
        if (stream) return stream;
        throw new Error('Camera & Microphone access are required to join the call.');
      }
    }
  };

  // Setup WebRTC Service Subscriptions
  useEffect(() => {
    webrtcMeetingService.setEvents({
      onStatusChange: (status) => {
        setConnectionStatus(status);
        if (status === 'connected') {
          setErrorMessage(null);
          setIncomingCallModal(null);
        } else if (status === 'disconnected' || status === 'failed') {
          setRemoteStream(null);
          onRemoteStreamReady(null);
          setConsentGranted(false);
          onConsentStatusChange(false);
        }
      },
      onRemoteStream: (stream) => {
        console.log('[OnlineMeetingRoom] Remote stream received:', stream.getTracks());
        setRemoteStream(stream);
        onRemoteStreamReady(stream);
      },
      onIncomingCallRequest: (callerId, accept, decline) => {
        setIncomingCallModal({
          callerId,
          accept: () => {
            accept();
            setIncomingCallModal(null);
          },
          decline: () => {
            decline();
            setIncomingCallModal(null);
          }
        });
      },
      onRecordingConsentRequest: (hName) => {
        setConsentRequestModal({ hostName: hName });
      },
      onRecordingConsentDelivered: () => {
        setIsRequestingConsent(false);
        setConsentDelivered(true);
      },
      onRecordingConsentResponse: (granted) => {
        setIsRequestingConsent(false);
        setConsentDelivered(false);
        setConsentGranted(granted);
        onConsentStatusChange(granted);
      },
      onRemoteMediaStateChange: (state) => {
        setRemoteMediaState(state);
      },
      onLatencyUpdate: (rtt) => {
        setRttLatencyMs(rtt);
      },
      onError: (err) => {
        setErrorMessage(err);
      }
    });

    // Duration timer updater
    timerRef.current = setInterval(() => {
      setCallDurationSec(webrtcMeetingService.getCallDuration());
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (activeLocalStreamRef.current && activeLocalStreamRef.current !== localStream) {
        activeLocalStreamRef.current.getTracks().forEach(t => t.stop());
        activeLocalStreamRef.current = null;
      }
      try {
        webrtcMeetingService.disconnect();
      } catch {}
    };
  }, []);

  // Sync video and audio DOM elements whenever streams or connection state changes
  useEffect(() => {
    const isConn = connectionStatus === 'connected';
    if (!isConn) return;

    const myLocal = activeLocalStream || localStream;
    const hostStream = role === 'host' ? myLocal : remoteStream;
    const guestStream = role === 'guest' ? myLocal : remoteStream;

    if (hostVideoRef.current && hostStream) {
      const currentSrc = hostVideoRef.current.srcObject as MediaStream | null;
      const currentTrack = currentSrc?.getVideoTracks()[0];
      const newTrack = hostStream.getVideoTracks()[0];
      if (newTrack && currentTrack?.id !== newTrack.id) {
        hostVideoRef.current.srcObject = hostStream;
        hostVideoRef.current.play().catch(e => console.warn('[Host Video Play Error]', e));
      }
    }

    if (guestVideoRef.current && guestStream) {
      const currentSrc = guestVideoRef.current.srcObject as MediaStream | null;
      const currentTrack = currentSrc?.getVideoTracks()[0];
      const newTrack = guestStream.getVideoTracks()[0];
      if (newTrack && currentTrack?.id !== newTrack.id) {
        guestVideoRef.current.srcObject = guestStream;
        guestVideoRef.current.play().catch(e => console.warn('[Guest Video Play Error]', e));
      }
    }

    if (remoteAudioRef.current && remoteStream) {
      const currentSrc = remoteAudioRef.current.srcObject as MediaStream | null;
      const currentTrack = currentSrc?.getAudioTracks()[0];
      const newTrack = remoteStream.getAudioTracks()[0];
      if (newTrack && currentTrack?.id !== newTrack.id) {
        remoteAudioRef.current.srcObject = remoteStream;
        remoteAudioRef.current.play().then(() => {
          setIsAudioAutoplayBlocked(false);
        }).catch(e => {
          console.warn('[Remote Audio Autoplay Blocked]', e);
          setIsAudioAutoplayBlocked(true);
        });
      }
    }
  }, [activeLocalStream, localStream, remoteStream, connectionStatus, role]);

  // Sync preview video element when not in call (only reassign if track ID differs!)
  useEffect(() => {
    if (connectionStatus !== 'connected' && previewVideoRef.current) {
      const myLocal = activeLocalStream || localStream;
      if (myLocal) {
        const currentSrc = previewVideoRef.current.srcObject as MediaStream | null;
        const currentTrack = currentSrc?.getVideoTracks()[0];
        const newTrack = myLocal.getVideoTracks()[0];
        if (newTrack && currentTrack?.id !== newTrack.id) {
          previewVideoRef.current.srcObject = myLocal;
          previewVideoRef.current.play().catch(() => {});
        }
      }
    }
  }, [activeLocalStream, localStream, connectionStatus]);

  // Draw QR Code whenever modal payload changes
  useEffect(() => {
    if (qrModalPayload && qrCanvasRef.current) {
      QRCode.toCanvas(qrCanvasRef.current, qrModalPayload, {
        width: 240,
        margin: 2,
        color: {
          dark: '#0f172a',
          light: '#ffffff'
        }
      });
    }
  }, [qrModalPayload]);

  // --- ACTIONS ---

  // Host: Start Room in AnyDesk Mode
  const handleHostStartRoom = async () => {
    setErrorMessage(null);
    try {
      const stream = await ensureLocalStream();
      await webrtcMeetingService.initHostBroker(meetingCode, stream);
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to initialize Host room.');
    }
  };

  // Guest: Join Room in AnyDesk Mode
  const handleGuestJoinRoom = async () => {
    const cleanCode = inputMeetingCode.trim();
    if (!cleanCode) {
      setErrorMessage('Please enter the 6-digit Host Room Code.');
      return;
    }
    setErrorMessage(null);
    try {
      const stream = await ensureLocalStream();
      await webrtcMeetingService.joinGuestBroker(cleanCode, stream);
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to connect to Host room.');
    }
  };

  // Manual Mode: Host creates Offer
  const handleCreateManualOffer = async () => {
    setErrorMessage(null);
    try {
      const stream = await ensureLocalStream();
      const code = await webrtcMeetingService.createManualOffer(stream);
      setGeneratedOfferCode(code);
      setManualStep('offer_created');
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to generate Offer code.');
    }
  };

  // Manual Mode: Guest accepts Offer and creates Answer
  const handleGuestGenerateAnswer = async () => {
    if (!inputOfferCode.trim()) {
      setErrorMessage('Please paste the Host Offer code.');
      return;
    }
    setErrorMessage(null);
    try {
      const stream = await ensureLocalStream();
      const ans = await webrtcMeetingService.acceptManualOffer(inputOfferCode.trim(), stream);
      setGeneratedAnswerCode(ans);
      setManualStep('answer_created');
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to process Offer code.');
    }
  };

  // Manual Mode: Host applies Answer
  const handleHostApplyAnswer = async () => {
    if (!inputAnswerCode.trim()) {
      setErrorMessage('Please paste the Guest Answer code.');
      return;
    }
    setErrorMessage(null);
    try {
      await webrtcMeetingService.applyManualAnswer(inputAnswerCode.trim());
      setManualStep('connected');
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to connect using Answer code.');
    }
  };

  // Disconnect Call
  const handleDisconnect = () => {
    webrtcMeetingService.disconnect();
    setRemoteStream(null);
    onRemoteStreamReady(null);
    setConsentGranted(false);
    setIsRequestingConsent(false);
    setConsentDelivered(false);
    onConsentStatusChange(false);
    setManualStep('initial');
    setIncomingCallModal(null);
    setConsentRequestModal(null);
    setIsAudioAutoplayBlocked(false);
  };

  // Toggle Mute Audio
  const handleToggleAudio = () => {
    const stream = activeLocalStream || localStream;
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioMuted(!audioTrack.enabled);
        webrtcMeetingService.broadcastMediaState(!audioTrack.enabled, isVideoMuted);
      }
    }
  };

  // Toggle Video
  const handleToggleVideo = () => {
    const stream = activeLocalStream || localStream;
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoMuted(!videoTrack.enabled);
        webrtcMeetingService.broadcastMediaState(isAudioMuted, !videoTrack.enabled);
      }
    }
  };

  // Host: Send Recording Consent Request
  const handleRequestConsent = () => {
    setIsRequestingConsent(true);
    setConsentDelivered(false);
    webrtcMeetingService.requestRecordingConsent(hostName);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const isConnected = connectionStatus === 'connected';

  return (
    <div className={`flex flex-col ${isFullscreen ? 'h-full flex-1 justify-between space-y-2' : 'space-y-4'}`}>
      {/* Top Header Card: Title, Connection Mode & STUN Explanation (Hidden in Fullscreen for Maximum Video Area) */}
      {!isFullscreen && (
        <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-purple-500/15 text-purple-400 border border-purple-500/30">
                <Radio className="w-5 h-5 animate-pulse" />
              </div>
              <div>
                <h2 className="text-base font-bold text-brand-text flex items-center gap-2">
                  Online Remote Guest Studio (WebRTC P2P)
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                    No Login Required
                  </span>
                </h2>
                <p className="text-xs text-brand-muted">
                  Connect 2 participants across different cities & internet connections. Zero server storage, 100% private.
                </p>
              </div>
            </div>
          </div>

          {/* STUN Traversal & Network Badge */}
          <div className="flex items-center gap-2 bg-brand-elevated/70 border border-brand-border/60 px-3 py-1.5 rounded-xl text-xs">
            <Wifi className="w-3.5 h-3.5 text-brand-secondary" />
            <span className="text-brand-muted">Network:</span>
            <span className="font-semibold text-brand-text">Direct P2P via Google STUN</span>
            <div
              className="group relative cursor-pointer text-brand-muted hover:text-brand-text"
              title="STUN info"
            >
              <Info className="w-3.5 h-3.5" />
              <div className="absolute right-0 top-6 w-72 p-2.5 rounded-xl bg-slate-900 text-white text-[11px] shadow-2xl border border-slate-700 hidden group-hover:block z-50 pointer-events-none leading-relaxed">
                💡 <strong>NAT Traversal Note:</strong> STUN allows direct P2P connections over standard home/office Wi-Fi. Networks with strict corporate firewalls or symmetric NATs may restrict direct connections without a TURN relay.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Notice */}
      {errorMessage && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
          <button
            onClick={() => setErrorMessage(null)}
            className="text-xs underline hover:text-white ml-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Pre-Call Setup View (When Not Connected) */}
      {!isConnected && (
        <div className="p-5 rounded-2xl bg-brand-surface border border-brand-border shadow-card space-y-5">
          {/* Step 1: Select Role (Host vs Guest) */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pb-4 border-b border-brand-border/60">
            <div>
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider block">
                Step 1: Choose Your Role
              </span>
              <p className="text-xs text-brand-text font-medium">Are you hosting this podcast, or joining as a remote guest?</p>
            </div>

            <div className="flex items-center p-1 rounded-xl bg-brand-elevated border border-brand-border/60">
              <button
                onClick={() => handleSelectRole('host')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  role === 'host'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-brand-muted hover:text-brand-text'
                }`}
              >
                <User className="w-3.5 h-3.5" />
                <span>Host (Create Meeting)</span>
              </button>

              <button
                onClick={() => handleSelectRole('guest')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  role === 'guest'
                    ? 'bg-brand-accent text-white shadow-sm'
                    : 'text-brand-muted hover:text-brand-text'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                <span>Guest (Join Meeting)</span>
              </button>
            </div>
          </div>

          {/* Recording Policy Banner based on selected role */}
          {role === 'host' ? (
            <div className="p-3.5 rounded-xl bg-purple-500/10 border border-purple-500/30 text-xs flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20 text-purple-300 shrink-0">
                <Radio className="w-4 h-4 animate-pulse" />
              </div>
              <div className="text-purple-200 leading-relaxed">
                <strong className="text-white block font-semibold">🎙️ Dual-Track Host Recording (Both Participants Recorded on Host PC):</strong>
                Recording is saved <strong>100% locally on your computer</strong>. Both your local camera/mic (Track 1) and your remote guest's audio & video stream (Track 2) will be captured simultaneously into separate files.
              </div>
            </div>
          ) : (
            <div className="p-3.5 rounded-xl bg-indigo-500/10 border border-indigo-500/30 text-xs flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-300 shrink-0">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <div className="text-indigo-200 leading-relaxed">
                <strong className="text-white block font-semibold">🔒 Host-Exclusive Recording Policy:</strong>
                All recording is managed and saved <strong>strictly on the Host's PC</strong>. Both Host and Guest audio/video will be recorded together on the Host's machine. Zero disk space or recording files are needed on your computer.
              </div>
            </div>
          )}

          {/* Local Camera & Mic Live Preview Card */}
          <div className="p-4 rounded-xl bg-brand-elevated/40 border border-brand-border/60 flex flex-col sm:flex-row items-center gap-4">
            <div className="relative w-44 aspect-video rounded-xl bg-black border border-brand-border/60 overflow-hidden shrink-0 shadow-inner">
              <video
                ref={previewVideoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover transition-transform duration-200 ${
                  hostVideoMirrored ? 'scale-x-[-1]' : ''
                }`}
              />
              <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-md bg-black/60 text-[10px] text-white font-semibold">
                Your Camera
              </div>
            </div>
            <div className="flex-1 space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <strong className="text-brand-text">Local Camera & Microphone Verified</strong>
              </div>
              <p className="text-brand-muted text-[11px] leading-relaxed">
                Your camera and microphone are live. Video is displayed in standard Normal View (un-mirrored).
              </p>
              <div className="flex items-center flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleToggleAudio}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                    isAudioMuted ? 'bg-red-600 text-white border-red-500' : 'bg-brand-surface text-brand-text border-brand-border'
                  }`}
                >
                  {isAudioMuted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5 text-emerald-400" />}
                  <span>{isAudioMuted ? 'Mic Muted' : 'Mic Active'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleToggleVideo}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                    isVideoMuted ? 'bg-red-600 text-white border-red-500' : 'bg-brand-surface text-brand-text border-brand-border'
                  }`}
                >
                  {isVideoMuted ? <VideoOff className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5 text-purple-400" />}
                  <span>{isVideoMuted ? 'Camera Off' : 'Camera On'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setHostVideoMirrored(!hostVideoMirrored)}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border transition-colors bg-brand-surface hover:bg-brand-elevated text-brand-text border-brand-border cursor-pointer"
                  title="Toggle Mirror view"
                >
                  <span>{hostVideoMirrored ? '🪞 Mirrored' : '👀 Normal View (No Mirror)'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Step 2: Connection Method Switcher (AnyDesk 6-Digit vs Serverless Manual/QR) */}
          <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
            <span className="text-xs font-bold text-brand-muted uppercase tracking-wider">
              Step 2: Connection Method
            </span>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setSignalingMode('anydesk')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  signalingMode === 'anydesk'
                    ? 'bg-brand-elevated border-brand-accent text-brand-accent font-bold'
                    : 'bg-transparent border-brand-border/60 text-brand-muted hover:text-brand-text'
                }`}
              >
                ⚡ AnyDesk Mode (6-Digit Code)
              </button>
              <button
                onClick={() => setSignalingMode('manual')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  signalingMode === 'manual'
                    ? 'bg-brand-elevated border-brand-accent text-brand-accent font-bold'
                    : 'bg-transparent border-brand-border/60 text-brand-muted hover:text-brand-text'
                }`}
              >
                📋 Manual Code & QR Code (Serverless)
              </button>
            </div>
          </div>

          {/* --- ANYDESK 6-DIGIT MODE --- */}
          {signalingMode === 'anydesk' && (
            <div className="p-5 rounded-xl bg-brand-elevated/40 border border-brand-border/60 space-y-4">
              {role === 'host' ? (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-bold text-brand-text">Your AnyDesk-Style Meeting Code</h3>
                      <p className="text-xs text-brand-muted">
                        Share this 6-digit code with your guest. When they connect, you'll receive an Accept prompt.
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        const newCode = Math.floor(100000 + Math.random() * 900000).toString();
                        setMeetingCode(newCode);
                      }}
                      disabled={connectionStatus === 'waiting' || connectionStatus === 'connecting'}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border/60 text-xs text-brand-muted hover:text-brand-text"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      <span>Generate New Code</span>
                    </button>
                  </div>

                  {/* Big Code Display */}
                  <div className="flex items-center gap-3">
                    <div className="px-6 py-3 rounded-2xl bg-brand-surface border-2 border-purple-500/40 text-2xl md:text-3xl font-black font-mono tracking-widest text-brand-text shadow-glow">
                      {meetingCode.slice(0, 3)} {meetingCode.slice(3)}
                    </div>

                    <button
                      onClick={() => copyToClipboard(meetingCode)}
                      className="flex items-center gap-2 px-4 py-3 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-xs font-semibold text-brand-text"
                    >
                      {copiedCode ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      <span>{copiedCode ? 'Copied!' : 'Copy Code'}</span>
                    </button>

                    <button
                      onClick={() => setQrModalPayload(meetingCode)}
                      className="p-3 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-text"
                      title="Show QR Code"
                    >
                      <QrCode className="w-5 h-5 text-purple-400" />
                    </button>
                  </div>

                  {/* Action / Status */}
                  <div className="pt-2">
                    {connectionStatus === 'idle' && (
                      <button
                        onClick={handleHostStartRoom}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer"
                      >
                        <Radio className="w-4 h-4" />
                        <span>START LISTENING FOR GUEST</span>
                      </button>
                    )}

                    {connectionStatus === 'waiting' && (
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping" />
                        <span>Room active! Waiting for guest to enter code <strong>{meetingCode}</strong>...</span>
                      </div>
                    )}

                    {connectionStatus === 'connecting' && (
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-400 animate-pulse" />
                        <span>Connecting with Guest PC via WebRTC STUN...</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-bold text-brand-text">Join Host's Podcast Room</h3>
                    <p className="text-xs text-brand-muted">
                      Enter the 6-digit meeting code provided by your Host.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-3 max-w-md">
                    <input
                      type="text"
                      maxLength={6}
                      value={inputMeetingCode}
                      onChange={(e) => setInputMeetingCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="e.g. 582914"
                      className="w-full px-4 py-3 rounded-xl bg-brand-surface border border-brand-border text-center text-xl font-mono font-bold tracking-widest text-brand-text focus:outline-none focus:border-brand-accent"
                    />

                    <button
                      onClick={handleGuestJoinRoom}
                      disabled={inputMeetingCode.length < 6 || connectionStatus === 'connecting'}
                      className="w-full sm:w-auto shrink-0 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-brand-accent hover:bg-brand-accent-hover disabled:opacity-50 text-white font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer"
                    >
                      <ArrowRight className="w-4 h-4" />
                      <span>CONNECT TO HOST</span>
                    </button>
                  </div>

                  {connectionStatus === 'connecting' && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs max-w-md">
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-400 animate-pulse" />
                      <span>Ringing Host PC... Waiting for Host to accept connection.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* --- MANUAL OFFER / ANSWER & QR CODE MODE (100% SERVERLESS) --- */}
          {signalingMode === 'manual' && (
            <div className="p-5 rounded-xl bg-brand-elevated/40 border border-brand-border/60 space-y-4">
              {role === 'host' ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-bold text-brand-text">Host: 1-Click Serverless Offer Exchange</h3>
                    <p className="text-xs text-brand-muted">
                      Generate an encrypted connection code or QR code to send to the guest via WhatsApp/chat.
                    </p>
                  </div>

                  {!generatedOfferCode ? (
                    <button
                      onClick={handleCreateManualOffer}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer"
                    >
                      <Layers className="w-4 h-4" />
                      <span>GENERATE INVITE OFFER CODE</span>
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="text-[11px] font-bold text-brand-muted uppercase block mb-1">
                          Step 1: Send This Code to Guest
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={generatedOfferCode}
                            className="flex-1 px-3 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs font-mono text-brand-muted truncate"
                          />
                          <button
                            onClick={() => copyToClipboard(generatedOfferCode)}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text font-semibold hover:bg-brand-elevated/80"
                          >
                            {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                            <span>Copy</span>
                          </button>
                          <button
                            onClick={() => setQrModalPayload(generatedOfferCode)}
                            className="p-2 rounded-xl bg-brand-elevated border border-brand-border text-brand-text"
                            title="Show QR Code"
                          >
                            <QrCode className="w-4 h-4 text-purple-400" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="text-[11px] font-bold text-brand-muted uppercase block mb-1">
                          Step 2: Paste Guest's Answer Code Here
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={inputAnswerCode}
                            onChange={(e) => setInputAnswerCode(e.target.value)}
                            placeholder="Paste Answer Code received from Guest..."
                            className="flex-1 px-3 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs font-mono text-brand-text focus:outline-none focus:border-brand-accent"
                          />
                          <button
                            onClick={handleHostApplyAnswer}
                            disabled={!inputAnswerCode.trim()}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer"
                          >
                            <Check className="w-3.5 h-3.5" />
                            <span>CONNECT</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-bold text-brand-text">Guest: Join via Invite Offer Code</h3>
                    <p className="text-xs text-brand-muted">
                      Paste the Host's Invite Code below to generate your Answer Code.
                    </p>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold text-brand-muted uppercase block mb-1">
                      Step 1: Paste Host Invite Code
                    </label>
                    <textarea
                      rows={2}
                      value={inputOfferCode}
                      onChange={(e) => setInputOfferCode(e.target.value)}
                      placeholder="Paste Offer code sent by Host..."
                      className="w-full px-3 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs font-mono text-brand-text focus:outline-none focus:border-brand-accent"
                    />
                    <div className="pt-2">
                      <button
                        onClick={handleGuestGenerateAnswer}
                        disabled={!inputOfferCode.trim()}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-accent hover:bg-brand-accent-hover disabled:opacity-50 text-white font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer"
                      >
                        <ArrowRight className="w-3.5 h-3.5" />
                        <span>GENERATE ANSWER CODE</span>
                      </button>
                    </div>
                  </div>

                  {generatedAnswerCode && (
                    <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 space-y-2">
                      <label className="text-[11px] font-bold text-emerald-400 uppercase block">
                        Step 2: Copy This Answer Code & Send Back to Host
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={generatedAnswerCode}
                          className="flex-1 px-3 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs font-mono text-brand-muted truncate"
                        />
                        <button
                          onClick={() => copyToClipboard(generatedAnswerCode)}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text font-semibold hover:bg-brand-elevated/80"
                        >
                          {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          <span>Copy</span>
                        </button>
                      </div>
                      <p className="text-[11px] text-brand-muted">
                        Once Host pastes this answer, your video call will connect automatically!
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- IN-CALL ACTIVE MEETING DASHBOARD (WHEN CONNECTED) --- */}
      {isConnected && (
        <div className={`p-4 sm:p-5 rounded-2xl bg-brand-surface border border-brand-border shadow-card flex flex-col ${isFullscreen ? 'flex-1 min-h-0 justify-between space-y-3' : 'space-y-4'}`}>
          {/* Top Bar: Connection State, Duration, Latency, Consent Status */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-brand-border/60">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                P2P Connected
              </span>

              <div className="flex items-center gap-1.5 text-xs text-brand-muted">
                <Clock className="w-3.5 h-3.5 text-brand-secondary" />
                <span className="font-mono text-brand-text font-semibold">
                  {formatDuration(callDurationSec)}
                </span>
              </div>

              {rttLatencyMs !== null && (
                <span className="text-[11px] font-mono text-brand-muted">
                  RTT: <strong className="text-brand-text">{rttLatencyMs}ms</strong>
                </span>
              )}
            </div>

            {/* Consent Badge */}
            <div className="flex items-center gap-2">
              {isAudioAutoplayBlocked && (
                <button
                  onClick={() => {
                    if (remoteAudioRef.current) {
                      remoteAudioRef.current.play();
                      setIsAudioAutoplayBlocked(false);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs shadow-md animate-bounce cursor-pointer"
                  title="Browser blocked audio autoplay, click to unmute"
                >
                  <Volume2 className="w-4 h-4 text-black" />
                  <span>Click to Unmute Audio</span>
                </button>
              )}

              {consentGranted ? (
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <span>Recording Consent Granted</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-medium bg-amber-500/10 text-amber-300 border border-amber-500/30">
                  <ShieldAlert className="w-4 h-4 text-amber-400" />
                  <span>Consent Required to Record</span>
                </span>
              )}

              {/* Fullscreen Studio Toggle Button */}
              {onToggleFullscreen && (
                <button
                  type="button"
                  onClick={onToggleFullscreen}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-text text-xs font-semibold transition-colors cursor-pointer"
                  title={isFullscreen ? 'Exit Fullscreen Studio (Esc)' : 'Expand to Fullscreen Studio'}
                >
                  {isFullscreen ? <Minimize2 className="w-3.5 h-3.5 text-brand-accent" /> : <Maximize2 className="w-3.5 h-3.5" />}
                  <span>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
                </button>
              )}

              {/* End Call Button */}
              <button
                onClick={handleDisconnect}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600/20 hover:bg-red-600/30 border border-red-500/40 text-red-400 hover:text-red-300 text-xs font-semibold transition-colors"
                title="Disconnect call"
              >
                <PhoneOff className="w-3.5 h-3.5" />
                <span>Disconnect</span>
              </button>
            </div>
          </div>

          {/* Dual Recording Destination & Storage Indicator */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-xl bg-brand-elevated/70 border border-brand-border text-xs">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-purple-400 shrink-0 animate-pulse" />
              <span className="text-brand-muted">Recording Storage:</span>
              <span className="font-bold text-brand-text">
                {role === 'host' ? '💾 Host PC Only (Recording Both Host & Guest Tracks)' : '☁️ Host PC Only (You do not need disk storage)'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[11px] font-mono">
              <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                Track 1: Host ({hostName})
              </span>
              <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                Track 2: Guest ({guestName})
              </span>
            </div>
          </div>

          {/* Dual In-Call Video Tiles (Left: Host, Right: Guest) */}
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 ${isFullscreen ? 'flex-1 min-h-0 items-center my-auto' : ''}`}>
            {/* Host Video Tile */}
            <div className={`relative aspect-video rounded-2xl bg-black border border-brand-border/60 overflow-hidden shadow-inner group ${isFullscreen ? 'w-full h-full max-h-[calc(100vh-270px)] flex items-center justify-center' : ''}`}>
              <video
                ref={hostVideoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover transition-transform duration-200 ${
                  hostVideoMirrored ? 'scale-x-[-1]' : ''
                }`}
              />

              {/* Badge: Host Info */}
              <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 text-xs text-white">
                <span className="w-2 h-2 rounded-full bg-purple-500" />
                <span className="font-semibold">{hostName} (Host)</span>
                {role === 'host' && <span className="text-[10px] text-white/60">(You)</span>}
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-mono font-medium border border-purple-500/30">
                  1080p Locked
                </span>
              </div>

              {/* Mirror Toggle Button */}
              <button
                type="button"
                onClick={() => setHostVideoMirrored(!hostVideoMirrored)}
                className="absolute bottom-3 left-3 z-10 px-2 py-1 rounded-lg bg-black/70 hover:bg-black/90 backdrop-blur-md text-[10px] text-white/80 hover:text-white border border-white/10 transition-colors flex items-center gap-1 cursor-pointer"
                title="Toggle Mirror/Normal view"
              >
                <span>{hostVideoMirrored ? '🪞 Mirrored' : '👀 Normal View'}</span>
              </button>

              {/* Audio/Video Mute Indicators */}
              <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                {(role === 'host' ? isAudioMuted : remoteMediaState.audioMuted) && (
                  <span className="p-1.5 rounded-lg bg-red-600/80 text-white" title="Microphone Muted">
                    <MicOff className="w-3.5 h-3.5" />
                  </span>
                )}
                {(role === 'host' ? isVideoMuted : remoteMediaState.videoOff) && (
                  <span className="p-1.5 rounded-lg bg-red-600/80 text-white" title="Camera Off">
                    <VideoOff className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>
            </div>

            {/* Guest Video Tile */}
            <div className={`relative aspect-video rounded-2xl bg-black border border-brand-border/60 overflow-hidden shadow-inner group ${isFullscreen ? 'w-full h-full max-h-[calc(100vh-270px)] flex items-center justify-center' : ''}`}>
              <video
                ref={guestVideoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover transition-transform duration-200 ${
                  guestVideoMirrored ? 'scale-x-[-1]' : ''
                }`}
              />

              {/* Badge: Guest Info */}
              <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 text-xs text-white">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="font-semibold">{guestName} (Remote Guest)</span>
                {role === 'guest' && <span className="text-[10px] text-white/60">(You)</span>}
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono font-medium border border-emerald-500/30">
                  1080p Locked
                </span>
              </div>

              {/* Mirror Toggle Button */}
              <button
                type="button"
                onClick={() => setGuestVideoMirrored(!guestVideoMirrored)}
                className="absolute bottom-3 left-3 z-10 px-2 py-1 rounded-lg bg-black/70 hover:bg-black/90 backdrop-blur-md text-[10px] text-white/80 hover:text-white border border-white/10 transition-colors flex items-center gap-1 cursor-pointer"
                title="Toggle Mirror/Normal view"
              >
                <span>{guestVideoMirrored ? '🪞 Mirrored' : '👀 Normal View'}</span>
              </button>

              {/* Audio/Video Mute Indicators */}
              <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                {(role === 'guest' ? isAudioMuted : remoteMediaState.audioMuted) && (
                  <span className="p-1.5 rounded-lg bg-red-600/80 text-white" title="Microphone Muted">
                    <MicOff className="w-3.5 h-3.5" />
                  </span>
                )}
                {(role === 'guest' ? isVideoMuted : remoteMediaState.videoOff) && (
                  <span className="p-1.5 rounded-lg bg-red-600/80 text-white" title="Camera Off">
                    <VideoOff className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Dedicated Hidden Audio Element for Remote Sound Output */}
          <audio ref={remoteAudioRef} autoPlay playsInline />

          {/* In-Call Controls Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-brand-border/60">
            {/* Left: Device Controls (Mute Mic / Camera) */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleAudio}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                  isAudioMuted
                    ? 'bg-red-600 text-white'
                    : 'bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-text'
                }`}
              >
                {isAudioMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                <span>{isAudioMuted ? 'Unmute Mic' : 'Mute Mic'}</span>
              </button>

              <button
                onClick={handleToggleVideo}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                  isVideoMuted
                    ? 'bg-red-600 text-white'
                    : 'bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-text'
                }`}
              >
                {isVideoMuted ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                <span>{isVideoMuted ? 'Turn Cam On' : 'Turn Cam Off'}</span>
              </button>
            </div>

            {/* Right: Recording & Consent Controls (For Host) */}
            <div className="flex items-center gap-2">
              {role === 'host' && (
                <>
                  {!consentGranted ? (
                    <button
                      onClick={handleRequestConsent}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-xs font-bold shadow-md transition-all active:scale-95 cursor-pointer ${
                        consentDelivered
                          ? 'bg-amber-600 hover:bg-amber-700'
                          : isRequestingConsent
                          ? 'bg-purple-700 opacity-90'
                          : 'bg-purple-600 hover:bg-purple-700'
                      }`}
                      title={consentDelivered ? 'Prompt delivered to guest. Click to resend if needed.' : 'Send recording consent prompt to guest'}
                    >
                      <ShieldCheck className={`w-4 h-4 ${isRequestingConsent ? 'animate-spin' : ''}`} />
                      <span>
                        {consentDelivered
                          ? 'PROMPT SENT! WAITING FOR GUEST (RESEND)'
                          : isRequestingConsent
                          ? 'SENDING REQUEST TO GUEST...'
                          : 'ASK GUEST RECORDING CONSENT'}
                      </span>
                    </button>
                  ) : (
                    !isRecordingActive ? (
                      <button
                        onClick={onStartRecording}
                        className="flex items-center gap-2 px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold shadow-glow animate-pulse active:scale-95 cursor-pointer"
                      >
                        <span className="w-2.5 h-2.5 rounded-full bg-white" />
                        <span>START DUAL-TRACK RECORDING</span>
                      </button>
                    ) : (
                      <button
                        onClick={onStopRecording}
                        className="flex items-center gap-2 px-5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-red-500/40 text-red-400 text-xs font-bold shadow-md active:scale-95 cursor-pointer"
                      >
                        <span className="w-2.5 h-2.5 rounded-sm bg-red-500" />
                        <span>STOP RECORDING</span>
                      </button>
                    )
                  )}
                </>
              )}

              {role === 'guest' && (
                <div className="text-xs">
                  {isRecordingActive ? (
                    <span className="text-red-400 font-bold flex items-center gap-2 px-3 py-1.5 rounded-xl bg-red-500/10 border border-red-500/30 animate-pulse">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping" />
                      Host is recording both Host & Guest audio and video locally on Host PC
                    </span>
                  ) : (
                    <span className="text-brand-muted italic flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-elevated/50 border border-brand-border/50">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      Connected to Host studio. Host records both participants on their PC.
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- ANYDESK-STYLE INCOMING CONNECTION POPUP (HOST) --- */}
      {incomingCallModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="w-full max-w-md p-6 rounded-2xl bg-brand-surface border-2 border-purple-500/50 shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-2xl bg-purple-500/20 text-purple-400 border border-purple-500/40 animate-bounce">
                <PhoneCall className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-brand-text">Incoming Connection Request</h3>
                <p className="text-xs text-brand-muted">
                  A remote guest is requesting to join your podcast session.
                </p>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-brand-elevated/70 border border-brand-border text-xs text-brand-muted">
              Peer ID: <span className="font-mono text-brand-text font-bold">{incomingCallModal.callerId}</span>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={incomingCallModal.decline}
                className="px-4 py-2 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-muted hover:text-brand-text text-xs font-semibold"
              >
                Decline
              </button>
              <button
                onClick={incomingCallModal.accept}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-md active:scale-95"
              >
                <UserCheck className="w-4 h-4" />
                <span>Accept & Connect</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- RECORDING CONSENT POPUP (GUEST) --- */}
      {consentRequestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="w-full max-w-md p-6 rounded-2xl bg-brand-surface border-2 border-brand-accent/50 shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-2xl bg-brand-accent/20 text-brand-accent border border-brand-accent/40">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-brand-text">Recording Consent Request</h3>
                <p className="text-xs text-brand-muted">
                  Host <strong>"{consentRequestModal.hostName}"</strong> wants to record this podcast episode.
                </p>
              </div>
            </div>

            <p className="text-xs text-brand-muted leading-relaxed">
              By granting consent, your audio and video stream will be recorded locally on the host's PC for editing and publishing.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  webrtcMeetingService.respondToRecordingConsent(false);
                  setConsentRequestModal(null);
                }}
                className="px-4 py-2 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-muted hover:text-brand-text text-xs font-semibold"
              >
                Decline
              </button>
              <button
                onClick={() => {
                  webrtcMeetingService.respondToRecordingConsent(true);
                  setConsentGranted(true);
                  onConsentStatusChange(true);
                  setConsentRequestModal(null);
                }}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shadow-md active:scale-95"
              >
                <Check className="w-4 h-4" />
                <span>Grant Consent & Record</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- QR CODE DISPLAY MODAL --- */}
      {qrModalPayload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="w-full max-w-xs p-6 rounded-2xl bg-white text-slate-900 shadow-2xl text-center space-y-4">
            <h3 className="text-base font-bold text-slate-900">Scan to Connect</h3>
            <p className="text-xs text-slate-500">
              Scan this QR code with another device or camera to join the meeting room.
            </p>

            <div className="flex justify-center p-2 bg-slate-50 rounded-xl border border-slate-200">
              <canvas ref={qrCanvasRef} className="rounded-lg" />
            </div>

            <button
              onClick={() => setQrModalPayload(null)}
              className="w-full py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
